import React, { useState, useEffect, useRef } from "react";
import "./LoginModal.css";

/**
 * LoginModal
 * Extracted from Login.jsx — same POST /users/login endpoint, same
 * localStorage pattern, no MUI, no full-page scaffolding.
 *
 * Props:
 *  - onClose: () => void
 *  - onSuccess: (user) => void   called after successful login so GlobalNav
 *                                 can update its currentUser state without
 *                                 a full page reload (optional — falls back
 *                                 to window.location.href = redirectUrl)
 *  - redirectUrl: string         where to go after login (default "/hub")
 */
export default function LoginModal({ onClose, onSuccess, redirectUrl = "/hub" }) {
  const [formData, setFormData] = useState({ username: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const overlayRef = useRef(null);
  const firstInputRef = useRef(null);

  // Focus first input on open
  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  // Close on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape") onClose();
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Prevent body scroll while open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  function handleChange(e) {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const res = await fetch("/users/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      const data = await res.json();

      if (data.success) {
        localStorage.setItem("user", JSON.stringify(data.user));
        if (onSuccess) {
          onSuccess(data.user);
        } else {
          window.location.href = redirectUrl;
        }
      } else {
        setError(data.message || "Invalid credentials");
      }
    } catch (err) {
      setError("An error occurred during login");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="lm-overlay"
      ref={overlayRef}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      role="dialog"
      aria-modal="true"
      aria-label="Sign in"
    >
      <div className="lm-panel">
        <button className="lm-close" onClick={onClose} aria-label="Close">
          ✕
        </button>

        <div className="lm-logo">MIHU</div>
        <h2 className="lm-title">Sign in</h2>

        {error && <div className="lm-error">{error}</div>}

        <form onSubmit={handleSubmit} noValidate>
          <div className="lm-field">
            <label className="lm-label" htmlFor="lm-username">Email</label>
            <input
              id="lm-username"
              className="lm-input"
              type="email"
              name="username"
              value={formData.username}
              onChange={handleChange}
              required
              autoComplete="email"
              ref={firstInputRef}
            />
          </div>

          <div className="lm-field">
            <label className="lm-label" htmlFor="lm-password">Password</label>
            <input
              id="lm-password"
              className="lm-input"
              type="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            className="lm-submit"
            disabled={loading}
          >
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="lm-footer">
          <a
            href="/register"
            className="lm-register-link"
          >
            Don't have an account? Register
          </a>
        </div>
      </div>
    </div>
  );
}
